import { useMemo } from 'react';
import { type BaseRowData, type DataProvider } from '@adv-frontend/data-provider';
import { useAsyncFlow } from '@adv-frontend/modules-flow';
import { type GridMobileRenderItem, type GridMobileRenderItemStub } from './lib/types';

type GridMobileRowProps<RowData extends BaseRowData> = {
    /** Виртуальный id строки из dataProvider */
    rowId: string;
    /** Порядковый номер строки в списке */
    listIndex: number;
    /** Flow-based провайдер данных строк */
    dataProvider: DataProvider<RowData>;
    /** Рендерер загруженной строки */
    renderItem: GridMobileRenderItem<RowData>;
    /** Рендерер стаба строки на время загрузки */
    renderItemStub: GridMobileRenderItemStub;
};

export function GridMobileRow<RowData extends BaseRowData>({
    rowId,
    listIndex,
    dataProvider,
    renderItem,
    renderItemStub,
}: GridMobileRowProps<RowData>) {
    /**
     * Подписка на поток строки по виртуальному id триггерит загрузку данных строки.
     * Провайдер в зависимостях обязателен: при смене queryArg фабрика возвращает новый
     * провайдер, и поток строки должен пересоздаться.
     */
    const rowDataFlow = useMemo(() => dataProvider.rowData(rowId), [dataProvider, rowId]);
    const rowDataResult = useAsyncFlow(rowDataFlow);

    if (rowDataResult.isError) {
        return null;
    }

    /** Построчно не суспендимся — на время загрузки строки рисуем стаб */
    if (!rowDataResult.isReady && !rowDataResult.isRefreshing) {
        return <>{renderItemStub(listIndex)}</>;
    }

    return <>{renderItem({ row: rowDataResult.current(), listIndex })}</>;
}
